import { usePaymentHistory } from "../hooks/usePlanQuery";
import { Table, Button, Skeleton } from "@/components";
import { Download, CreditCard, Calendar, CheckCircle2, Clock, XCircle } from "lucide-react";
import { useState } from "react";
import { Pagination } from "../../whats-app-crm/components/ui/Pagination";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";


interface PaymentHistoryProps {
    hideHeader?: boolean;
}

const PaymentHistory = ({ hideHeader = false }: PaymentHistoryProps) => {
    const {id:chatBotId} = useParams()
    const { data: response, isLoading } = usePaymentHistory(chatBotId!);
    const historyData = response?.data.data || [];
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Calculate pagination
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = historyData.slice(startIndex, endIndex);
    const totalPages = Math.ceil(historyData.length / pageSize);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(1);
    };

    // Create pagination object for the Pagination component
    const pagination = {
        page: currentPage,
        perPage: pageSize,
        total: historyData.length,
        totalPages,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1
    };

    const getStatusIcon = (status: string) => {
        switch (status.toLowerCase()) {
            case 'paid':
                return <CheckCircle2 className="w-3 h-3 text-emerald-500 dark:text-emerald-500" />;
            case 'pending':
                return <Clock className="w-3 h-3 text-amber-500 dark:text-amber-400" />;
            case 'failed':
                return <XCircle className="w-3 h-3 text-red-700 dark:text-red-400" />;
            case 'created':
                return <Clock className="w-3 h-3 text-blue-700 dark:text-blue-400" />;
            default:
                return null;
        }
    };

    const columns = [
        {
            key: 'plan',
            header: 'Plan',
            render: (_: any, row: any) => (
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                        <CreditCard className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex flex-col">
                        <span className="font-medium text-slate-700 dark:text-gray-300">
                            {row.plan?.name || "N/A"}
                        </span>
                        {row.billingCycle && (
                            <span className="text-[10px] text-gray-400 capitalize">
                                {row.billingCycle}
                            </span>
                        )}
                    </div>
                </div>
            )
        },
        {
            key: 'amount',
            header: 'Amount',
            render: (value: number, row: any) => (
                <span className="font-semibold text-slate-700 dark:text-gray-300">
                    ₹{value.toLocaleString()}
                    {row.billingCycle && (
                        <span className="text-xs text-gray-400 ml-1">
                            /{row.billingCycle === 'yearly' ? 'yr' : 'mo'}
                        </span>
                    )}
                </span>
            )
        },
        {
            key: 'paymentMethod',
            header: 'Payment Via',
            render: (value: string | null, _: any) => (
                <div className="flex items-center gap-2 text-slate-700 dark:text-gray-300">
                    {value ? (
                        <>
                            <CreditCard className="w-4 h-4" />
                            <span className="capitalize">{value}</span>
                        </>
                    ) : (
                        <span className="text-gray-400">-</span>
                    )}
                </div>
            )
        },
        {
            key: 'subscriptionPeriod',
            header: 'Subscription',
            render: (_: any, row: any) => {
                const period = row.subscriptionPeriod;
                if (!period || !period.startDate) {
                    return <span className="text-gray-400">{period?.status}</span>;
                }
                return (
                    <div className="flex flex-col text-xs">
                        <span className="text-slate-700 dark:text-gray-300">
                            {new Date(period.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {' - '}
                            {new Date(period.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className={`capitalize ${period.status === 'active' ? 'text-green-600' : 'text-gray-400'}`}>
                            {period.status}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'createdAt',
            header: 'Order Date',
            render: (value: string) => (
                <div className="flex items-center gap-2 text-slate-700 dark:text-gray-300">
                    <Calendar className="w-4 h-4 " />
                    {new Date(value).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </div>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (value: string) => (
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${value.toLowerCase() === 'paid' ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border border-emerald-100 dark:border-emerald-600/60' :
                    value.toLowerCase() === 'pending' ? 'bg-amber-50 text-amber-500 dark:text-amber-400 border border-amber-100' :
                    value.toLowerCase() === 'failed' ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-500 border border-red-100 dark:border-red-600/60' :
                        'bg-blue-50 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-100  dark:border-blue-500/20'
                    }`}>
                    {getStatusIcon(value)}
                    {value.charAt(0).toUpperCase() + value.slice(1)}
                </div>
            )
        },
        {
            key: 'invoiceUrl',
            header: 'Invoice',
            render: (value: string, row: any) => (
                <Button
                    variant="outline"
                    size="sm"
                    disabled={['created', 'pending', 'failed'].includes(row.status.toLowerCase())}
                    className="h-8 gap-2 border-slate-200 hover:bg-slate-50 transition-colors"
                    onClick={() => window.open(import.meta.env.VITE_API_URL + value)}
                >
                    <Download className="w-3.5 h-3.5" />
                    Download
                </Button>
            )
        }
    ];

    return (
        <div className={cn(!hideHeader && "container-full py-6 mx-auto w-full")}>
            {!hideHeader && (
                <div className="mb-6">
                    <h1 className="text-3xl font-bold tracking-tight text-dark dark:text-white">
                        Payment History
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 mt-1">
                        View and manage your transaction records
                    </p>
                </div>
            )}
            <div>
                <div className="flex flex-col gap-6">
                    <div>
                        {isLoading ? (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm!">
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {Array.from({ length: pageSize }).map((_, index) => (
                                        <div key={index} className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3 flex-1">
                                                    <Skeleton className="w-10 h-10 rounded-lg" />
                                                    <Skeleton className="h-4 w-32" />
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <Skeleton className="h-4 w-20" />
                                                    <Skeleton className="h-4 w-24" />
                                                    <Skeleton className="h-6 w-20 rounded-full" />
                                                    <Skeleton className="h-8 w-24 rounded" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : historyData.length > 0 ? (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm!">
                                <Table
                                    columns={columns}
                                    data={paginatedData}
                                    pagination={{ showPagination: false }}
                                    className="rounded-t-sm! rounded-b-none"
                                />
                                <Pagination
                                    pagination={pagination}
                                    onPageChange={handlePageChange}
                                    pageSize={pageSize}
                                    onPageSizeChange={handlePageSizeChange}
                                />
                            </div>
                        ) : (
                            <div className="p-16 text-center dark:text-slate-300">
                                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Clock className="w-8 h-8 text-slate-400 dark:text-slate-300" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No transactions found</h3>
                                <p className="text-slate-500 dark:text-slate-300">You haven't made any payments yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentHistory;